import { EventEmitter } from '@angular/core';
import { IRequestCallbackFunction } from '@df/api/request/request.service';
import { LocalStorageService } from '@df/browser/storage/local-storage.service';
import { Product } from '@df/catalog/product/product';
import { ProductResource } from '@df/catalog/product/product.resource';
import { Status } from '@df/core/status';
import { NgInject } from '@df/ng/ng-inject';
import { BasketService, IResponseAddToBasket } from '@df/session/api/basket/basket.service';
import { TranslationService } from '@df/ui/translation/translation.service';
import { Nexus } from '@tomandco/nexus-api-types';
import chunk from 'lodash-es/chunk';
import map from 'lodash-es/map';
import pickBy from 'lodash-es/pickBy';
import reduce from 'lodash-es/reduce';

export interface IPriceOptions {
    // if to check for the orginal price
    orgPrice: boolean;
}

export interface IBulkOrderOptions {
    // if set then selection will be kept in local storage
    storage: boolean;
}

export interface IBulkQties {
    [productId: number]: number;
}

export class BulkOrder {
    /**
     * Local storage entry name
     */
    static storageName = 'bulkOrder';

    /**
     * Change emmiter
     */
    readonly change = new EventEmitter<void>();

    /**
     * Products qties
     */
    protected qties: IBulkQties = {};

    /**
     * Product resource
     */
    @NgInject(ProductResource)
    protected productResource!: ProductResource;

    /**
     * Basket service
     */
    @NgInject(BasketService)
    private basketService!: BasketService;

    /**
     * Basket service
     */
    @NgInject(LocalStorageService)
    private localStorageService!: LocalStorageService;

    /**
     * Translation service
     */
    @NgInject(TranslationService)
    private translationService!: TranslationService;

    /**
     * Bulko processor options
     */
    private options: IBulkOrderOptions = {
        storage: false
    };

    constructor(options?: Partial<IBulkOrderOptions>) {
        if (options) {
            this.options = Object.assign(this.options, options);
        }
        // initiate storage
        if (this.options.storage) {
            this.loadFromLocalStorage();
            this.change.subscribe(() => {
                this.saveToLocalStorage();
            });
        }
    }

    //////////////////////////
    ///
    /// QTIES
    ///
    //////////////////////////

    /**
     * Product qty setter
     */
    getQty(item: Product | number | string): number {
        const product = this.toProduct(item);
        if (!product) {
            return 0;
        }
        if (product.isConfigurable) {
            return reduce(product.directChildrenIds, (memo: number, childId) => memo + this.getQty(childId), 0);
        }
        return this.qties[this.toId(item)] || 0;
    }

    /**
     * Product qty getter
     */
    setQty(item: Product | number | string, qty: number): void {
        const product = this.toProduct(item);
        if (!product || product.isConfigurable) {
            qty = 0;
        }
        if (this.getQty(item) !== qty) {
            this.qties[this.toId(item)] = qty;
            this.change.emit();
        }
    }

    /**
     * Remove everything from bulkd order
     */
    reset(qties: IBulkQties = {}) {
        this.qties = qties;
        this.change.next();
    }

    /**
     * Takes aggegated qty
     */
    getTotalQty(): number {
        return reduce(
            this.qties,
            (sum: number, qty: number) => {
                if (qty) {
                    sum += qty;
                }
                return sum;
            },
            0
        );
    }

    //////////////////////////
    ///
    /// PRICING
    ///
    //////////////////////////

    /**
     * Takes row price of the product
     */
    getRowPrice(item: Product | number | string, options?: IPriceOptions): number {
        const product = this.toProduct(item);
        if (!product) {
            return 0;
        }
        if (product.isConfigurable) {
            return reduce(product.directChildrenIds, (memo: number, childId) => memo + this.getRowPrice(childId, options), 0);
        }

        const qty = this.getQty(item);
        if (qty) {
            return qty * ((options?.orgPrice ? product.getOrgPrice() : product.getPrice()) || 0);
        }
        return 0;
    }

    /**
     * Takes aggegated price
     */
    getTotalPrice(options?: IPriceOptions): number {
        return reduce(
            this.qties,
            (sum: number, qty: number, id: string) => {
                if (qty) {
                    sum += this.getRowPrice(id, options);
                }
                return sum;
            },
            0
        );
    }

    //////////////////////////
    ///
    /// BASKET INTEGRATION
    ///
    //////////////////////////

    protected getBasketRequests(): Nexus.Lib.Basket.BasketRequestData[] {
        return map(this.qties, (qty: number, id: string) => {
            if (qty) {
                return { id: Number(id), qty };
            }
        }).filter(i => !!i) as Nexus.Lib.Basket.BasketRequestData[];
    }

    /**
     * Submits current selection to be added to basket
     */
    async submit(status: Status, onSuccess?: IRequestCallbackFunction): Promise<void> {
        const requests = this.getBasketRequests();

        if (requests.length) {
            status.busy = true;
            try {
                let lastResult: IResponseAddToBasket | void = undefined;
                for (const chunkRequests of chunk(requests, 10)) {
                    lastResult = await this.basketService.add(status, chunkRequests);
                }
                if (lastResult) {
                    onSuccess?.(lastResult);
                }
                this.reset();
                status.success = 'SUCCESS';
            } catch (err) {
                status.error = err as any;
            }
        }
    }

    /**
     * Retrives item errors
     */
    getError(item: Product | number | string): string | undefined {
        const product = this.toProduct(item);
        if (!product) {
            return undefined;
        }
        if (product.isConfigurable) {
            if (product.directChildrenIds.find(i => this.getError(i))) {
                return 'Some products are unavailable';
            } else {
                return undefined;
            }
        }

        const qty = this.getQty(item);
        if (qty > 0) {
            if (!product.isSalable(qty)) {
                const maxQty = product.getStockQty(true);
                if (maxQty) {
                    return this.translationService.transform('only % available', maxQty);
                }
                return this.translationService.transform('out of stock');
            }
        }
        return undefined;
    }

    /**
     * Retrieves list of product that have errors
     */
    hasErrors(): boolean {
        return !!this.getSelectedItems().find(i => this.getError(i));
    }

    /**
     * Retrieves list of product that have errors
     */
    getErroringItems(): Product[] {
        return this.getSelectedItems().filter(i => this.getError(i));
    }

    /**
     * Takes all selected items
     */
    getSelectedItems(): Product[] {
        return this.productResource.getAllExisting(Object.keys(this.qties).map(id => Number(id))).filter(i => this.getQty(i) > 0);
    }

    //////////////////////////
    ///
    /// STORAGE
    ///
    //////////////////////////

    /**
     * Saves qties to local storage
     */
    private saveToLocalStorage() {
        this.localStorageService.set(BulkOrder.storageName, this.qties);
    }

    /**
     * Loads qties from local storage
     */
    private loadFromLocalStorage() {
        const qties = this.localStorageService.get(BulkOrder.storageName);
        if (qties && typeof qties === 'object') {
            this.qties = pickBy(qties, (value, key) => !!(typeof value === 'number' && typeof key === 'string' && key.match(/^\d+$/)));
        }
    }

    //////////////////////////
    ///
    /// HELPERS
    ///
    //////////////////////////

    /**
     * Normalize item to product model
     */
    protected toProduct(item: Product | number | string): Product | undefined {
        if (typeof item === 'string') {
            item = Number(item);
        }
        return typeof item === 'number' ? (this.productResource.getExisting(item) as Product) : item;
    }

    /**
     * Normalize item to its id
     */
    private toId(item: Product | number | string): number {
        switch (typeof item) {
            case 'number':
                return item;
            case 'string':
                return Number(item);
            default:
                return item.id;
        }
    }
}
